import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { PostCard } from "@/components/PostCard";
import { CATEGORY_META, CATEGORY_ORDER, FILTER_TAGS, tx } from "@/lib/i18n";
import { Search as SearchIcon } from "lucide-react";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const { lang } = useLang();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [state, setState] = useState(params.get("state") || "");
  const [qualification, setQualification] = useState(params.get("qualification") || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (state) qs.set("state", state);
    if (qualification) qs.set("qualification", qualification);
    setParams(qs, { replace: true });
    setLoading(true);
    api.get(`/search?${qs}`).then((r) => { setItems(r.data.items); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, state, qualification]);

  return (
    <div>
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            {lang === "hi" ? "अधिसूचनाएँ खोजें" : "Search Notifications"}
          </h1>
          <div className="mt-6 flex bg-white text-slate-900 rounded-md overflow-hidden border border-white/20">
            <div className="pl-4 grid place-items-center text-slate-400"><SearchIcon size={18} /></div>
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tx("ui.search_placeholder", lang)}
              className="flex-1 h-12 px-3 text-sm outline-none"
              autoFocus
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-8 lg:grid-cols-4">
        <aside className="lg:col-span-1 space-y-6">
          <div>
            <div className="text-xs font-bold tracking-[0.14em] uppercase text-slate-500 mb-2">
              {lang === "hi" ? "श्रेणी" : "Category"}
            </div>
            <select
              data-testid="filter-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full h-10 border border-slate-300 rounded-sm text-sm px-2 bg-white"
            >
              <option value="">{tx("ui.all", lang)}</option>
              {CATEGORY_ORDER.map((s) => (
                <option key={s} value={s}>{CATEGORY_META[s][lang === "hi" ? "hi" : "en"]}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-xs font-bold tracking-[0.14em] uppercase text-slate-500 mb-2">
              {lang === "hi" ? "योग्यता / सेक्टर" : "Qualification / Sector"}
            </div>
            <select
              data-testid="filter-qualification"
              value={qualification}
              onChange={(e) => setQualification(e.target.value)}
              className="w-full h-10 border border-slate-300 rounded-sm text-sm px-2 bg-white"
            >
              <option value="">{tx("ui.all", lang)}</option>
              {FILTER_TAGS.map((t) => (
                <option key={t.slug} value={t.slug}>{t[lang === "hi" ? "hi" : "en"]}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-xs font-bold tracking-[0.14em] uppercase text-slate-500 mb-2">
              {lang === "hi" ? "राज्य" : "State"}
            </div>
            <input
              data-testid="filter-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder={lang === "hi" ? "जैसे उत्तर प्रदेश" : "e.g. Uttar Pradesh"}
              className="w-full h-10 border border-slate-300 rounded-sm text-sm px-3 bg-white"
            />
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="text-xs text-slate-500 mb-4 font-mono-tabular">
            {loading ? "…" : items.length} {lang === "hi" ? "परिणाम" : "results"}
          </div>
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[0,1,2,3].map(i => <div key={i} className="h-40 bg-white border border-slate-200 rounded-md animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500" data-testid="search-empty">
              {tx("ui.no_results", lang)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
